import { inject } from 'aurelia-framework';
import { NewInstance } from 'aurelia-dependency-injection';
import { ValidationController, validateTrigger, ValidationRules, ValidateBindingBehavior, ValidationRenderer } from 'aurelia-validation';
import { activationStrategy } from 'aurelia-router';

import { User } from '../models/user';
import { UserService } from '../Advertiser.SharedPortal/services/user-service';
import { Router } from 'aurelia-router';
import { StatusService } from '../Advertiser.SharedPortal/services/status-service';
import { MultiObserverService } from '../Advertiser.SharedPortal/services/multi-observer-service';
import { LoggerService } from '../Advertiser.SharedPortal/services/logger-service';
import { BrowserStorageService } from '../Advertiser.SharedPortal/services/browser-storage-service';


var config = require('../../config.js');
var safeStringify = require('fast-safe-stringify');

@inject( NewInstance.of(ValidationController), Router, UserService, StatusService, MultiObserverService, LoggerService, BrowserStorageService )
export class UserConfirmation {
   constructor(controller, router, userService, statusService, multiObserver, loggerService, browserStorage) {
      this.controller = controller;
      this.router = router;
      this.userService = userService;
      this.status = statusService;
      this.multiObserver = multiObserver;
      this.logger = loggerService;
      this.browserStorage = browserStorage;

      this.controller.validateTrigger = validateTrigger.manual;
      this.emailAddress = '';
      this.verificationCode = '';
      this.errorMessage = '';
      this.showError = false;
      this.oldPassword = '';
      this.newPassword = '';
      this.newPassword2 = '';
      this.loginUrl = '#/login';
      
      this.myKeypressCallback = this.keypressInput.bind(this);
   }

   activate(params, routeConfig) {
      let that = this;
      this.verificationCode = params.code;
      this.oldPassword = Buffer.from( params.password, 'base64' ).toString();
      this.emailAddress = Buffer.from( params.email, 'base64' ).toString();
      this.multiObserver.observe( this, ['newPassword', 'newPassword2'], function () { that.status.dirty(); });
      window.addEventListener( 'keypress', this.myKeypressCallback, false );
      this.logger.debug( 'Logging out user (if logged in)' );
      this.userService.logout( true );
      this.configStatus();
   }

   deactivate() {
      window.removeEventListener( 'keypress', this.myKeypressCallback );
   }

   configStatus() {
      // statuses:  dontchange, clean, dirty, working, loading, saving, success, error
      this.status.texts = ['', 'Log In', 'Log In', 'Logging In', 'Logging In', '', 'Logged In', 'Error'];
      this.status.classes = ['', 'btn-disabled', 'btn-enabled', 'btn-processing', '', '', 'btn-success', 'btn-error'];
      this.status.clean();
   }

   keypressInput(e) {
      if (e.key === 'Enter') {
         this.onSubmit();
      }
   }

   onSubmit() {
      console.log('onSubmit()');
      let that = this;
      let errors = this.controller.validate()
         .then((err) => {
            if (err.valid) {
               this.status.working();
               this.errorMessage = '';
               this.controller.reset();
               this.logger.debug( 'Confirming signup:', this.emailAddress, this.verificationCode );
               
               this.userService.confirmSignup(this.emailAddress, this.verificationCode)
                  .then(response => {
                     if (response && response[0] !== 'Success') {
                        that.errorMessage = response;

                        if (that.errorMessage[0].includes('Invalid code provided')) {
                           that.errorMessage[0] = 'Your account has already been confirmed. To access your account, please login normally.';
                           that.status.set(this.status.statuses.error, this.status.statuses.clean, config.buttonReset);
                        }
                     }
                     else {
                        var user = new User();
                        user.emailAddress = that.emailAddress;
                        user.password = this.oldPassword;
                        this.logger.debug( 'Logging in new user under temporary password:', user );

                        that.userService.login( user )
                           .then( response => {
                              that.logger.debug( 'Login returns:', response );

                              if (response && response[0] == 'Success') {
                                 let token = this.browserStorage.getItem('AccessToken');
                                 this.logger.debug( 'Changing password:', token, this.oldPassword, this.newPassword );

                                 this.userService.changePassword( token, this.oldPassword, this.newPassword, this.newPassword2 )
                                    .then( response => {
                                       that.logger.debug( 'Change password returns:', response );
                                       
                                       if (response && response[0] !== 'Success') {
                                          that.errorMessage = "The was a problem changing your password.  Either try the link in your signup email again, or contact Delty Support please.";
                                          that.status.set(this.status.statuses.error, this.status.statuses.clean, config.buttonReset);
                                       }
                                       else {
                                          that.status.clean();
                                          this.userService.redirectToPortal();
                                       }
                                    });
                              } else {
                                 that.errorMessage = 'There was a problem logging into your account with the temporary password.  Please contact Delty Support.';
                                 that.status.set(this.status.statuses.error, this.status.statuses.clean, config.buttonReset);
                              }
                           });
                     }
                  });
            } else {
		console.log('Error: ' + JSON.stringify(that.controller.errors));
	    }
         });
   }

   onLogin() {
      this.router.navigateToRoute('login', { emailaddress: this.emailAddress });
   }
}

var passwordRegex = /(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\,><':;|_~`]).{8,}/;
var passwordFormatMessage = 'Sorry, your {which} password is formatted incorrectly. Passwords must be at least 8 characters including one number, one upper, one lowercase letter, and a special character: ^ $ * . [ ] { } ( ) ? - " ! @ # % & / , > < \' : ; | _ ~ `'; 
var passwordMatchMessage = 'Sorry, your passwords don\'t match. Please re-enter your passwords.';

ValidationRules
   .ensure(p => p.oldPassword).required()
   .ensure(p => p.newPassword).required()
   .ensure(p => p.oldPassword).matches( passwordRegex ).withMessage( passwordFormatMessage.replace('{which}', 'temporary') ) 
   .ensure(p => p.newPassword).matches( passwordRegex ).withMessage( passwordFormatMessage.replace('{which}', 'new') )
   .ensure(p => p.newPassword2).required().withMessage('Re-entered Password is required')
   .ensure(p => p.newPassword2).satisfies((value, obj) => value === obj.newPassword).withMessage( passwordMatchMessage )
   .ensure(p => p.emailAddress).required()
   .ensure(p => p.emailAddress).email().withMessage( 'Email address is improperly formated (please ensure it is all lowercase)')
   .ensure(p => p.verificationCode).required().minLength(6)
   .on(UserConfirmation);
